import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const goals = ["Fat Loss", "Muscle Gain", "Strength", "Body Recomposition", "Athletic Performance"];
const timelines = ["ASAP", "1–3 months", "3–6 months", "6+ months"];

export default function ApplicationForm() {
  const [form, setForm] = useState({ name: "", phone: "", email: "", goal: "", timeline: "" });
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = () => {
    if (!form.name || !form.email) return;
    setSubmitted(true);
  };

  return (
    <section className="py-24 md:py-32 bg-[var(--dvx-surface)] transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16 items-start">

          {/* Left */}
          <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            <span className="text-[var(--dvx-primary)] font-oswald text-sm uppercase tracking-widest">
              Application
            </span>
            <h2 className="mt-4 font-oswald text-4xl md:text-5xl font-bold text-[var(--dvx-text)] uppercase tracking-tight">
              Apply For Coaching
            </h2>
            <p className="mt-6 text-[var(--dvx-text-muted)] font-manrope text-lg leading-relaxed">
              Fill out the short application below. If you're a strong fit, you'll be invited for a consultation.
            </p>

            {/* Best for block */}
            <div className="mt-8 border border-[var(--dvx-border)] p-6">
              <p className="font-oswald text-sm uppercase tracking-widest text-[var(--dvx-text-muted)] mb-4">Best for:</p>
              <ul className="space-y-2">
                {[
                  "People serious about physical transformation",
                  "Those ready to commit for 12–16 weeks",
                  "Anyone who wants structure + accountability",
                  "Clients who want 1-on-1 premium coaching",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-[var(--dvx-text-muted)] font-manrope text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-[var(--dvx-primary)] mt-2 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* Right — form */}
          <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
            {submitted ? (
              <div className="bg-[var(--dvx-bg)] border border-[var(--dvx-primary)] p-10 text-center">
                <div className="w-14 h-14 bg-[var(--dvx-primary)]/10 border border-[var(--dvx-primary)]/30 flex items-center justify-center mx-auto mb-4">
                  <ArrowRight size={24} className="text-[var(--dvx-primary)]" />
                </div>
                <h3 className="font-oswald text-2xl font-bold text-[var(--dvx-text)] uppercase mb-2">Application Received</h3>
                <p className="text-[var(--dvx-text-muted)] font-manrope">We'll review your application and reach out within 24 hours.</p>
              </div>
            ) : (
              <div className="bg-[var(--dvx-bg)] border border-[var(--dvx-border)] p-8 space-y-5">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-oswald text-xs uppercase tracking-widest text-[var(--dvx-text-muted)] mb-2">Full Name</label>
                    <input
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      className="w-full bg-[var(--dvx-surface)] border border-[var(--dvx-border)] px-4 py-3 text-[var(--dvx-text)] font-manrope text-sm placeholder:text-[var(--dvx-text-muted)] focus:outline-none focus:border-[var(--dvx-primary)] transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block font-oswald text-xs uppercase tracking-widest text-[var(--dvx-text-muted)] mb-2">Phone Number</label>
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="Your number"
                      className="w-full bg-[var(--dvx-surface)] border border-[var(--dvx-border)] px-4 py-3 text-[var(--dvx-text)] font-manrope text-sm placeholder:text-[var(--dvx-text-muted)] focus:outline-none focus:border-[var(--dvx-primary)] transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-oswald text-xs uppercase tracking-widest text-[var(--dvx-text-muted)] mb-2">Email Address</label>
                  <input
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="you@example.com"
                    className="w-full bg-[var(--dvx-surface)] border border-[var(--dvx-border)] px-4 py-3 text-[var(--dvx-text)] font-manrope text-sm placeholder:text-[var(--dvx-text-muted)] focus:outline-none focus:border-[var(--dvx-primary)] transition-colors"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block font-oswald text-xs uppercase tracking-widest text-[var(--dvx-text-muted)] mb-2">Main Goal</label>
                    <select
                      name="goal"
                      value={form.goal}
                      onChange={handleChange}
                      className="w-full bg-[var(--dvx-surface)] border border-[var(--dvx-border)] px-4 py-3 text-[var(--dvx-text)] font-manrope text-sm focus:outline-none focus:border-[var(--dvx-primary)] transition-colors"
                    >
                      <option value="">Select goal</option>
                      {goals.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="block font-oswald text-xs uppercase tracking-widest text-[var(--dvx-text-muted)] mb-2">Timeline</label>
                    <select
                      name="timeline"
                      value={form.timeline}
                      onChange={handleChange}
                      className="w-full bg-[var(--dvx-surface)] border border-[var(--dvx-border)] px-4 py-3 text-[var(--dvx-text)] font-manrope text-sm focus:outline-none focus:border-[var(--dvx-primary)] transition-colors"
                    >
                      <option value="">Select timeline</option>
                      {timelines.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  className="w-full py-4 bg-gradient-to-r from-[var(--dvx-primary)] to-[var(--dvx-primary-dark)] text-white font-oswald font-bold uppercase tracking-widest hover:scale-[1.02] transition-transform flex items-center justify-center gap-2"
                >
                  Apply Now <ArrowRight size={18} />
                </button>

                <p className="text-xs text-[var(--dvx-text-muted)] font-manrope text-center">
                  We only take clients who can execute.
                </p>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
